import React, { FC, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Journey } from 'src/common/interfaces/journey.interface';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetJourney } from 'src/utils/journeyApi';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import {
  BreadcrumbsConfig,
  JourneyMetrics,
  MetricCard,
  TimePeriodKey,
  TimePeriodOption,
  TimePeriodParam,
} from '../../constants';
import { convertEngagementsToMetrics } from '../../helpers/convert-engagements-to-metrics';
import { DashboardInsightsItemLayout } from '../../item-layout';
import { DashboardInsightsJourneysJourneyContent } from './content';
import { useEngagementsMetrics } from '../use-engagements-metrics.hook';
import { Engagement } from 'src/editor/insights/constants';
import { STEPS_VIEWED_CARD, EMAILS_CAPTURED_CARD, CTA_CLICKS_CARD } from 'src/constants';
import { createCompletionRateCard, createJourneyOpensCard } from '../../helpers/metric-cards';
import useDashboardActiveTab from 'src/dashboard/helpers/active-tab.hook';
import JourneyListbox from 'src/common/form/JourneyListbox';
import { createTimePeriodOptionAllTime, createTimePeriodOptionLast30Days } from '../../helpers/time-periods';
import { useJourneyMetrics } from '../use-journey-metrics.hook';
import { getDate30DaysAgo } from 'src/utils/date';
import { DashboardInsightsJourneysTabType } from '../constants';
import { Helmet } from 'react-helmet';
import JoyRideOnboardingGuide from 'src/onboarding/joyride-onboarding';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { useJourneys } from 'src/store/journeys';
import { LockClosedIcon } from '@heroicons/react/outline';

const INSIGHT_CARDS = [STEPS_VIEWED_CARD, EMAILS_CAPTURED_CARD, CTA_CLICKS_CARD];

const TIME_PERIODS: TimePeriodOption[] = [createTimePeriodOptionLast30Days(), createTimePeriodOptionAllTime()];

const getEngagementMetrics = (engagements: Engagement[], previousEngagements?: Engagement[]): MetricCard[] =>
  convertEngagementsToMetrics(INSIGHT_CARDS, engagements, previousEngagements);

const getCompletionPercentage = (metrics?: JourneyMetrics) => metrics?.average_completion_percentage || 0;
const getSessionCount = (metrics?: JourneyMetrics) => metrics?.num_sessions || 0;

type DashboardInsightsJourneysJourneyProps = {
  journeyUuid: string;
  activeTabType?: DashboardInsightsJourneysTabType;
};

export const DashboardInsightsJourneysJourney: FC<DashboardInsightsJourneysJourneyProps> = ({
  journeyUuid,
  activeTabType = 'steps',
}) => {
  useDashboardActiveTab('insights');
  const [journey, setJourney] = useState<Journey>();
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [domLoaded, setDomLoaded] = useState(false);

  const history = useHistory();
  const { currentOrganization } = useCurrentOrganization();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { features, showUpgradeModal } = useFeatureRestrictions();
  const journeys = useJourneys((state) => state.journeys);
  const [timePeriodKey, setTimePeriodKey] = useState<TimePeriodKey>('all_time');
  const timePeriodOption = TIME_PERIODS.find((tp) => tp.key === timePeriodKey);
  const engagementFilters = useMemo(() => ({ time_period: timePeriodOption?.param }), [timePeriodOption]);
  const { fetching: fetchingEngagements, engagements } = useEngagementsMetrics({
    journeyId: journey?.id,
    filters: engagementFilters,
  });
  const metricsFilters = useMemo(() => ({ time_period: timePeriodOption?.param }), [timePeriodOption]);
  const { fetching: fetchingMetrics, metrics } = useJourneyMetrics({
    journeyId: journey?.id,
    filters: metricsFilters,
  });

  const shouldShowLastMonthDelta = timePeriodOption?.key === 'last_30_days';
  const lastMonthTimePeriodParam: TimePeriodParam | undefined = useMemo(() => {
    if (!shouldShowLastMonthDelta || !timePeriodOption) {
      return;
    }
    const lastMonthEndTime = timePeriodOption.param.start_time;
    if (!lastMonthEndTime) {
      return;
    }
    return {
      end_time: timePeriodOption?.param.start_time,
      start_time: getDate30DaysAgo(new Date(lastMonthEndTime)).toISOString(),
    };
  }, [shouldShowLastMonthDelta, timePeriodOption]);
  const metricsFiltersLastMonth = useMemo(
    () => (lastMonthTimePeriodParam ? { time_period: lastMonthTimePeriodParam } : undefined),
    [lastMonthTimePeriodParam]
  );
  const { fetching: fetchingMetricsLastMonth, metrics: metricsLastMonth } = useJourneyMetrics({
    journeyId: journey?.id,
    filters: metricsFiltersLastMonth,
  });

  const engagementFiltersLastMonth = useMemo(
    () => (lastMonthTimePeriodParam ? { time_period: lastMonthTimePeriodParam } : undefined),
    [lastMonthTimePeriodParam]
  );
  const { fetching: fetchingEngagementsLastMonth, engagements: engagementsLastMonth } = useEngagementsMetrics({
    journeyId: journey?.id,
    filters: engagementFiltersLastMonth,
  });

  const breadcrumbsConfig: BreadcrumbsConfig = {
    items: [
      {
        value: 'Insights',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/insights`);
        },
      },
      {
        value: 'Journeys',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/insights/journeys`);
        },
      },
      { value: journey?.name || '' },
    ],
  };

  const fetchJourney = async () => {
    try {
      setLoadingJourney(true);
      const fetchedJourney = await apiGetJourney(journeyUuid);
      setJourney(fetchedJourney);
      
      // Check if user can access basic insights (available on all plans)
      if (!features.canAccessBasicInsights) {
        showUpgradeModal(
          'basic_insights',
          'You need access to basic insights to view journey analytics. Please contact support.',
          'Insights Access Required'
        );
        // Redirect back to journeys list
        history.push(`/${currentOrganization.slug}/journeys`);
        return;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingJourney(false);
    }
  };

  useEffect(() => {
    fetchJourney();

    const handler = setTimeout(() => {
      setDomLoaded(true);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };

  }, [journeyUuid]);

  const loading = loadingJourney || fetchingMetrics || fetchingEngagements;

  if (!journey) {
    if (loading) {
      return <FixedLoadingSpinner />;
    }
    return <div>Cannot load journey insights</div>;
  }

  const metricCards: MetricCard[] = !loading
    ? [
        createCompletionRateCard(
          getCompletionPercentage(metrics),
          shouldShowLastMonthDelta && !fetchingMetricsLastMonth
            ? getCompletionPercentage(metrics) - getCompletionPercentage(metricsLastMonth)
            : 0
        ),
        createJourneyOpensCard(
          getSessionCount(metrics),
          shouldShowLastMonthDelta && !fetchingMetricsLastMonth
            ? getSessionCount(metrics) - getSessionCount(metricsLastMonth)
            : 0
        ),
        ...getEngagementMetrics(
          engagements,
          shouldShowLastMonthDelta && !fetchingEngagementsLastMonth ? engagementsLastMonth : undefined
        ),
      ]
    : [];

  const headerSection = journey ? (
    <div className='flex gap-6 w-full'>
      <h1 className='text-bedrock-serif-h1 line-clamp-1'>{journey.name}</h1>
      <JourneyListbox
        listBoxClassName='w-[240px]'
        containerClassName='min-w-fit'
        options={TIME_PERIODS}
        onChange={(option: TimePeriodOption) => setTimePeriodKey(option.key)}
        selected={timePeriodOption}
        renderValue={(option: TimePeriodOption) => option.label}
        renderKey={(option: TimePeriodOption) => option.key}
      />
    </div>
  ) : null;

  const contentSection = !loading ? (
    <DashboardInsightsJourneysJourneyContent
      journey={journey}
      activeTabType={activeTabType}
      timePeriodParam={timePeriodOption?.param}
    />
  ) : null;

  return (
    <>
      <Helmet>
        <title>Journey | Insights | Journey | {journey.name}</title>
      </Helmet>

       <JoyRideOnboardingGuide 
         initial={false} 
         journey={journey}
         stepIndexNumber={domLoaded ? undefined : -1}
       /> 

      <DashboardInsightsItemLayout
        headerSection={headerSection}
        breadcrumbsConfig={breadcrumbsConfig}
        metricCards={metricCards}
        contentSection={contentSection}
      />
    </>
  );
};
